// Geometry and coordinate calculation utilities for PdfTableViewer

/**
 * Convert canvas coordinates to PDF coordinates
 */
export const convertCanvasToPdfCoordinates = (
  canvasX,
  canvasY,
  canvasRef,
  pdfPageDimensions
) => {
  if (!canvasRef?.current || !pdfPageDimensions?.current) {
    return null;
  }

  const canvas = canvasRef.current;
  const canvasRect = canvas.getBoundingClientRect();
  const { width: pdfWidth, height: pdfHeight } = pdfPageDimensions.current;

  if (!pdfWidth || !pdfHeight) {
    return null;
  }

  // Calculate scale factors
  const pdfToCanvasScaleX = canvas.width / pdfWidth;
  const pdfToCanvasScaleY = canvas.height / pdfHeight;
  const cssScaleX = canvasRect.width / canvas.width;
  const cssScaleY = canvasRect.height / canvas.height;

  // Convert canvas coordinates to PDF coordinates
  const pdfX = canvasX / cssScaleX / pdfToCanvasScaleX;
  const pdfY = canvasY / cssScaleY / pdfToCanvasScaleY;

  return { x: pdfX, y: pdfY };
};

/**
 * Calculate word boundaries and center from bbox
 * Handles both normalized (≤1) and absolute coordinates, and different bbox formats
 */
export const calculateWordBounds = (bbox, pdfPageDimensions) => {
  if (!bbox || !pdfPageDimensions?.current) {
    return null;
  }

  const { width: pdfWidth, height: pdfHeight } = pdfPageDimensions.current;

  if (!pdfWidth || !pdfHeight) {
    return null;
  }

  let wordLeft, wordRight, wordTop, wordBottom;

  // Handle different bbox formats
  if (
    bbox.left !== undefined &&
    bbox.top !== undefined &&
    bbox.width !== undefined &&
    bbox.height !== undefined
  ) {
    // Reducto format: {left, top, width, height}
    if (
      bbox.left <= 1 &&
      bbox.top <= 1 &&
      bbox.width <= 1 &&
      bbox.height <= 1
    ) {
      // Normalized coordinates
      wordLeft = bbox.left * pdfWidth;
      wordRight = (bbox.left + bbox.width) * pdfWidth;
      wordTop = bbox.top * pdfHeight;
      wordBottom = (bbox.top + bbox.height) * pdfHeight;
    } else {
      // Absolute coordinates
      wordLeft = bbox.left;
      wordRight = bbox.left + bbox.width;
      wordTop = bbox.top;
      wordBottom = bbox.top + bbox.height;
    }
  } else {
    // Standard format: {x0, y0, x1, y1} or {x, y, right, bottom}
    const x0 = bbox.x0 || bbox.x || 0;
    const y0 = bbox.y0 || bbox.y || 0;
    const x1 = bbox.x1 || bbox.right || 0;
    const y1 = bbox.y1 || bbox.bottom || 0;

    if (x0 <= 1 && y0 <= 1 && x1 <= 1 && y1 <= 1) {
      // Normalized coordinates
      wordLeft = x0 * pdfWidth;
      wordRight = x1 * pdfWidth;
      wordTop = y0 * pdfHeight;
      wordBottom = y1 * pdfHeight;
    } else {
      // Absolute coordinates
      wordLeft = x0;
      wordRight = x1;
      wordTop = y0;
      wordBottom = y1;
    }
  }

  // Calculate centers
  const centerX = (wordLeft + wordRight) / 2;
  const centerY = (wordTop + wordBottom) / 2;

  return {
    left: wordLeft,
    right: wordRight,
    top: wordTop,
    bottom: wordBottom,
    centerX,
    centerY,
    width: wordRight - wordLeft,
    height: wordBottom - wordTop,
  };
};

/**
 * Check if a point is inside a shape
 */
export const isPointInShape = (shape, x, y) => {
  if (!shape) return false;

  switch (shape.type) {
    case "rectangle": {
      const { x: shapeX, y: shapeY, width, height } = shape;
      const minX = Math.min(shapeX, shapeX + width);
      const maxX = Math.max(shapeX, shapeX + width);
      const minY = Math.min(shapeY, shapeY + height);
      const maxY = Math.max(shapeY, shapeY + height);
      return x >= minX && x <= maxX && y >= minY && y <= maxY;
    }
    case "circle": {
      const { centerX, centerY, radius } = shape;
      const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
      return distance <= radius;
    }
    case "polygon":
    case "freehand": {
      const { points } = shape;
      if (points.length < 3) return false;

      // Ray casting algorithm
      let inside = false;
      for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        if (
          points[i].y > y !== points[j].y > y && // eslint-disable-line no-mixed-operators
          x <
            ((points[j].x - points[i].x) * (y - points[i].y)) /
              (points[j].y - points[i].y) +
              points[i].x
        ) {
          inside = !inside;
        }
      }
      return inside;
    }
    default:
      return false;
  }
};

/**
 * Find words within a given shape
 */
export const findWordsInShape = (
  shape,
  selectedTable,
  currentPage,
  pdfPageDimensions
) => {
  if (!selectedTable || !shape) return [];

  const wordsInShape = [];

  // Get all words from all cells in the current page
  Object.keys(selectedTable).forEach((cellKey) => {
    if (cellKey.match(/^\d+_\d+$/)) {
      const cellLocationData = selectedTable[cellKey];
      if (cellLocationData && Array.isArray(cellLocationData)) {
        const firstCellInfo = cellLocationData[0];
        if (
          firstCellInfo &&
          firstCellInfo.page === currentPage &&
          firstCellInfo.words
        ) {
          firstCellInfo.words.forEach((word, wordIndex) => {
            const bounds = calculateWordBounds(word.bbox, pdfPageDimensions);
            if (!bounds) return;

            // Check if word center is in shape
            if (isPointInShape(shape, bounds.centerX, bounds.centerY)) {
              wordsInShape.push({
                cellKey,
                wordIndex,
                word,
                centerX: bounds.centerX,
                centerY: bounds.centerY,
              });
            }
          });
        }
      }
    }
  });

  return wordsInShape;
};
